import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { Edit, Delete, Add, ArrowBack, Info, LocationOn } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

const ShiftPage = () => {
  // State for pagination and data
  const [currentPage, setCurrentPage] = useState(1);
  const [shifts, setShifts] = useState([]); // Initially empty; will be fetched from backend
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingShift, setEditingShift] = useState(null);

  // Our form uses keys matching the backend's column names
  const [formData, setFormData] = useState({
    shift_name: "",
    shift_hours: 8,
    start_time: "09:00",  // using 24-hour format (e.g. "09:00")
    buffer_time: 60,
    working_days: "Mon-Fri",
    weekly_hours: 40,
    weekly_off_days: "Sat-Sun",
    status: "Active",
    default: false,
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");

  const shiftsPerPage = 10;
  const totalPages = Math.ceil(shifts.length / shiftsPerPage);
  const displayedShifts = shifts.slice(
    (currentPage - 1) * shiftsPerPage,
    currentPage * shiftsPerPage
  );

  // Base URL for your shift API endpoints
  const BASE_API_URL = "https://attendanceapi.gensparc.com/shift";

  // Fetch shifts from backend API
  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BASE_API_URL}/shiftlist`, config);
      setShifts(response.data);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  // Reset current page if needed
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [shifts, totalPages, currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectShift = useCallback((shiftId) => {
    setSelectedShifts((prev) =>
      prev.includes(shiftId)
        ? prev.filter((id) => id !== shiftId)
        : [...prev, shiftId]
    );
  }, []);

  // Delete shift(s) by calling the DELETE endpoint for each selected shift
  const handleDelete = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      for (const id of selectedShifts) {
        await axios.delete(`${BASE_API_URL}/shiftdelete/${id}`, config);
      }
      fetchShifts();
      setSelectedShifts([]);
    } catch (error) {
      console.error("Error deleting shift(s):", error);
    }
  }, [selectedShifts]);

  // When clicking Create/Edit, prepopulate form fields if editing
  const handleAddShiftClick = (shift = null) => {
    if (shift) {
      setFormData({
        shift_name: shift.shift_name,
        shift_hours: shift.shift_hours,
        start_time: shift.start_time,
        // We do not update end_time here as it is computed on the backend.
        buffer_time: shift.buffer_time,
        working_days: shift.working_days,
        weekly_hours: shift.weekly_hours,
        weekly_off_days: shift.weekly_off_days,
        status: shift.status,
        default: shift.default,
      });
    } else {
      setFormData({
        shift_name: "",
        shift_hours: 8,
        start_time: "09:00",
        buffer_time: 60,
        working_days: "Mon-Fri",
        weekly_hours: 40,
        weekly_off_days: "Sat-Sun",
        status: "Active",
        default: false,
      });
    }
    setEditingShift(shift);
    setShowAddForm(true);
  };

  // Submit the form: If editingShift exists, update via PUT; otherwise, create via POST.
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Payload keys must match what the backend expects
      const payload = {
        shift_name: formData.shift_name,
        shift_hours: formData.shift_hours,
        start_time: formData.start_time,
        buffer_time: formData.buffer_time,
        working_days: formData.working_days,
        weekly_hours: formData.weekly_hours,
        weekly_off_days: formData.weekly_off_days,
        status: formData.status,
        default: formData.default,
      };

      if (editingShift) {
        // Update existing shift using PUT endpoint
        await axios.put(`${BASE_API_URL}/shiftupdate/${editingShift.id}`, payload, config);
      } else {
        // Create new shift using POST endpoint
        await axios.post(`${BASE_API_URL}/shiftadd`, payload, config);
      }

      fetchShifts();
      setShowAddForm(false);
      setEditingShift(null);
      // Reset form fields
      setFormData({
        shift_name: "",
        shift_hours: 8,
        start_time: "09:00",
        buffer_time: 60,
        working_days: "Mon-Fri",
        weekly_hours: 40,
        weekly_off_days: "Sat-Sun",
        status: "Active",
        default: false,
      });
    } catch (error) {
      console.error("Error submitting shift:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleCellClick = (content) => {
    setDialogContent(content);
    setOpenDialog(true);
  };

  const commonStyles = {
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
    padding: "8px",
    textAlign: "center",
  };

  // In rendering, use the keys as returned by the backend
  return (
    <div style={{ marginRight: "0", paddingLeft: "5px", paddingRight: "5px" }}>
      <div className="container-fluid px-0 py-5">
        <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 3 }}>
          <div className="d-flex justify-content-between align-items-center">
            <Typography variant="h4" component="h1" color="textPrimary" sx={{ fontWeight: 600 }}>
              Shift Management
            </Typography>
            <div>
              <Button
                onClick={() => handleAddShiftClick()}
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Create Shift
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                startIcon={<Delete />}
                disabled={selectedShifts.length === 0}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>

        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
              <TableRow>
                <TableCell sx={commonStyles}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      setSelectedShifts(e.target.checked ? shifts.map((shift) => shift.id) : []);
                    }}
                    checked={selectedShifts.length > 0 && selectedShifts.length === shifts.length}
                  />
                </TableCell>
                {[
                  "Shift Name",
                  "Shift Hours (hrs)",
                  "Start Time",
                  "End Time",
                  "Buffer Time (mins)",
                  "Working Days",
                  "Weekly Hours (hrs)",
                  "Weekly Off Days",
                  "Status",
                  "Action",
                ].map((heading, index) => (
                  <TableCell key={index} sx={commonStyles}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedShifts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={commonStyles}>
                    No shifts found
                  </TableCell>
                </TableRow>
              ) : (
                displayedShifts.map((shift) => (
                  <TableRow key={shift.id}>
                    <TableCell sx={commonStyles}>
                      <input
                        type="checkbox"
                        checked={selectedShifts.includes(shift.id)}
                        onChange={() => handleSelectShift(shift.id)}
                      />
                    </TableCell>
                    <TableCell sx={commonStyles}>{shift.shift_name}</TableCell>
                    <TableCell sx={commonStyles}>{shift.shift_hours}</TableCell>
                    <TableCell sx={commonStyles}>{shift.start_time}</TableCell>
                    <TableCell sx={commonStyles}>{shift.end_time}</TableCell>
                    <TableCell sx={commonStyles}>{shift.buffer_time}</TableCell>
                    <TableCell sx={commonStyles}>{shift.working_days}</TableCell>
                    <TableCell sx={commonStyles}>{shift.weekly_hours}</TableCell>
                    <TableCell sx={commonStyles}>{shift.weekly_off_days}</TableCell>
                    <TableCell sx={commonStyles}>{shift.status}</TableCell>
                    <TableCell sx={commonStyles}>
                      <IconButton
                        sx={{
                          backgroundColor: "#ffeb3b",
                          borderRadius: "10px",
                          "&:hover": { backgroundColor: "#fbc02d" },
                        }}
                        onClick={() => handleAddShiftClick(shift)} // Edit shift
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <Typography variant="body1">Page {currentPage} of {totalPages}</Typography>
          <div>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              sx={{
                marginRight: 1,
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Previous Page"
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              sx={{
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Next Page"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog for Full Content */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Full Content</DialogTitle>
        <DialogContent>
          <p>{dialogContent}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Shift Dialog */}
      <Dialog open={showAddForm} onClose={() => setShowAddForm(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => setShowAddForm(false)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">{editingShift ? "Edit Shift" : "Create Shift"}</Typography>
        </DialogTitle>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          <TextField
            fullWidth
            label="Shift Name*"
            name="shift_name"
            value={formData.shift_name}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Shift Hours (hrs)*"
            name="shift_hours"
            type="number"
            value={formData.shift_hours}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Start Time*"
            name="start_time"
            type="time"
            value={formData.start_time}
            onChange={handleInputChange}
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />
          {/* End Time is computed on the backend */}
          <TextField
            fullWidth
            label="Buffer Time (mins)*"
            name="buffer_time"
            type="number"
            value={formData.buffer_time}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Working Days*"
            name="working_days"
            value={formData.working_days}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Weekly Hours (hrs)*"
            name="weekly_hours"
            type="number"
            value={formData.weekly_hours}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Weekly Off Days*"
            name="weekly_off_days"
            value={formData.weekly_off_days}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Status*"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            margin="normal"
            required
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.default}
                onChange={handleCheckboxChange}
                name="default"
              />
            }
            label="Mark this as default shift"
          />

          <DialogActions sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {editingShift ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default ShiftPage;
